// src/config/chatgpt.js
export const CHATGPT_CONFIG = {
    // 環境に応じたエンドポイント設定
    API_ENDPOINT: import.meta.env.DEV
        ? 'http://localhost:3000/api/chatgpt'  // ローカル開発環境
        : '/api/chatgpt',                       // 本番環境（Vercel）

    // プロンプトテンプレート
    PROMPTS: {
        WEBSITE_DESIGN: `あなたは優秀なWebデザイナー兼フロントエンド開発者です。
以下の要望に基づいて、モダンで美しいWebサイトのHTMLコードを生成してください。

ユーザーの要望: {userRequest}

以下の形式で回答してください：

## HTML (完全なHTMLファイル)
\`\`\`html
<!DOCTYPE html>
<html lang="ja">
<head>
    <meta charset="UTF-8">
    <meta name="viewport" content="width=device-width, initial-scale=1.0">
    <title>ページタイトル</title>
    <style>
        /* CSSスタイル */
    </style>
    <!-- その他のhead要素 -->
</head>
<body>
    <!-- メインコンテンツ -->
    <header>
        <!-- ヘッダーコンテンツ -->
    </header>
    <main>
        <!-- メインコンテンツ -->
    </main>
    <footer>
        <!-- フッターコンテンツ -->
    </footer>
</body>
</html>
\`\`\`

## 説明
{explanation}

HTMLセクションは必ず上記の形式で出力し、コードブロック内に完全なHTMLファイルを記述してください。
DOCTYPE宣言、html、head、bodyタグを含む完全なHTML構造を生成してください。
CSSはstyleタグ内に記述し、JavaScriptが必要な場合はscriptタグも含めてください。`,

        COMMON_PART_DESIGN: `あなたは優秀なWebデザイナー兼フロントエンド開発者です。
以下の要望に基づいて、{partType}のHTMLコードとCSSコードを生成してください。

ユーザーの要望: {userRequest}

以下の形式で回答してください：

## HTML
\`\`\`html
{htmlCode}
\`\`\`

## CSS
\`\`\`css
{cssCode}
\`\`\`

## 説明
{explanation}

HTMLセクションには{partType}のコンテンツのみを記述してください。
CSSセクションには{partType}のスタイルのみを記述してください。
レスポンシブデザインを考慮し、モダンで美しいデザインにしてください。`,

        CODE_OPTIMIZATION: `以下のコードを最適化し、改善案を提案してください：

{code}

改善点と最適化されたコードを提供してください。`
    }
};

// ChatGPT API呼び出し関数（環境に応じて切り替え）
export const callChatGPT = async (prompt, systemMessage = null) => {
    try {

        // ローカル開発環境でOpenAI APIキーが設定されている場合は直接呼び出し
        if (import.meta.env.DEV && import.meta.env.VITE_OPENAI_API_KEY) {
            return await callOpenAIDirectly(prompt, systemMessage);
        }

        // Vercel Function経由で呼び出し
        return await callVercelFunction(prompt, systemMessage);

    } catch (error) {
        console.error('❌ ChatGPT API呼び出しエラー:', error);
        console.error('エラータイプ:', error.constructor.name);
        console.error('エラーメッセージ:', error.message);
        console.error('エラースタック:', error.stack);
        throw error;
    }
};

// OpenAI APIを直接呼び出す関数（ローカル開発環境用）
const callOpenAIDirectly = async (prompt, systemMessage = null) => {
    try {
        const apiKey = import.meta.env.VITE_OPENAI_API_KEY;
        if (!apiKey) {
            throw new Error('ローカル開発環境でOpenAI APIキーが設定されていません');
        }

        const response = await fetch('https://api.openai.com/v1/chat/completions', {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${apiKey}`,
            },
            body: JSON.stringify({
                model: 'gpt-4',
                messages: [
                    ...(systemMessage ? [{ role: 'system', content: systemMessage }] : []),
                    { role: 'user', content: prompt }
                ],
                max_tokens: 4000,
                temperature: 0.7
            })
        });

        if (!response.ok) {
            const errorData = await response.json();
            throw new Error(`OpenAI API エラー: ${errorData.error?.message || response.statusText}`);
        }

        const data = await response.json();
        return data.choices[0]?.message?.content || '応答が空です。';
    } catch (error) {
        console.error('ローカルOpenAI API呼び出しエラー:', error);
        throw error;
    }
};

// Vercel Function経由で呼び出す関数
const callVercelFunction = async (prompt, systemMessage = null) => {
    const response = await fetch(CHATGPT_CONFIG.API_ENDPOINT, {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
        },
        body: JSON.stringify({
            userRequest: prompt,
            systemMessage
        })
    });


    if (!response.ok) {
        const errorText = await response.text();
        console.error('❌ HTTPエラー:', response.status, errorText);
        throw new Error(`ChatGPT API HTTP エラー: ${response.status} - ${errorText}`);
    }

    // レスポンスの内容を確認
    const responseText = await response.text();
    console.log('📄 レスポンス内容:', responseText);

    if (!responseText || responseText.trim() === '') {
        throw new Error('ChatGPT APIからのレスポンスが空です');
    }

    let data;
    try {
        data = JSON.parse(responseText);
    } catch (parseError) {
        console.error('❌ JSONパースエラー:', parseError);
        console.error('パース対象テキスト:', responseText);
        throw new Error(`ChatGPT APIレスポンスのJSONパースに失敗: ${parseError.message}`);
    }

    if (!data.success) {
        throw new Error(`ChatGPT API エラー: ${data.message || '不明なエラー'}`);
    }

    console.log('✅ ChatGPT API呼び出し成功');
    console.log('レスポンスデータ:', data);

    return data.data || '応答が空です。';
};

// レスポンスからコードを抽出する関数
export const extractCodeFromResponse = (response) => {
    const result = {
        fullHTML: '',
        explanation: ''
    };

    try {
        // HTML (完全なHTMLファイル) を抽出
        const htmlMatch = response.match(/```html\s*([\s\S]*?)\s*```/);
        if (htmlMatch) {
            result.fullHTML = htmlMatch[1].trim();
        }

        // 説明を抽出
        const explanationMatch = response.match(/## 説明\s*([\s\S]*?)(?=\n##|$)/);
        if (explanationMatch) {
            result.explanation = explanationMatch[1].trim();
        }

    } catch (error) {
        console.error('コード抽出エラー:', error);
    }

    return result;
}; 